/**
 * The public template-gallery reads (D2.2, ARCFE-1026).
 *
 * These run on the app's UNAUTHENTICATED `globalHttp`, injected through
 * `configureTemplatesData` — NOT `getScribeHttpClients()`. The gallery is
 * readable without a session, so resolving it from the scribe registry would
 * attach auth interceptors to a deliberately anonymous read.
 *
 * This is the first real consumer of the seam's community half; up to now only
 * its queryClient was used (ARCFE-1018).
 */
import type {
  DetailedPublicTemplateResponse,
  GetPublicTemplatesListResponse,
} from '../../contracts';
import { getTemplatesDataConfig } from '../config';

export const getTemplateDetails = (urlPath: string) => {
  return getTemplatesDataConfig().communityHttp.get<DetailedPublicTemplateResponse>(
    `/template-gallery/templates/${urlPath}`
  );
};

export interface GetPublicTemplatesParams {
  filter?: (string | undefined)[];
  search?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
  specialtyGroups?: string[];
}

export const communityTemplatePageSize = 18;

/**
 * The gallery LIST read (D2.7, ARCFE-1046), moved VERBATIM.
 *
 * Every line of the query-string assembly is wire behaviour, not style: the
 * repeated `filter` appends, the unconditional `page_size`, and above all
 * `enable_recommendation` being the STRING `'false'` when a search term is
 * present and `'true'` otherwise. Rewriting any of that as an object literal
 * would change the emitted query string, so the `URLSearchParams` construction
 * is preserved exactly and the suite asserts the serialised result.
 */
export const getPublicTemplates = (params: GetPublicTemplatesParams) => {
  const searchParams = new URLSearchParams();
  params.filter?.forEach((filter) => {
    if (filter) {
      searchParams.append('filter', filter);
    }
  });
  if (params.search) {
    searchParams.append('search', params.search);
  }
  if (params.sort) {
    searchParams.append('sort', params.sort);
  }
  if (params.page) {
    searchParams.append('page', params.page.toString());
  }
  searchParams.append('page_size', communityTemplatePageSize.toString());

  searchParams.append('enable_recommendation', params.search ? 'false' : 'true');

  params.specialtyGroups?.forEach((group) => {
    searchParams.append('specialty_groups', group);
  });

  return getTemplatesDataConfig().communityHttp.get<GetPublicTemplatesListResponse>(
    `/template-gallery/templates/`,
    { params: searchParams }
  );
};
